import {useEffect, useState, type ReactNode} from 'react';
import {Check, Copy} from 'lucide-react';
import {
  formatDate,
  type GroupDetail,
} from '@internal/pages/group-pipeline/components/pipelineDisplay';
import {GroupNotes} from '@internal/pages/group-pipeline/detail/GroupNotes';

export function GroupOverviewTab({group}: {group: GroupDetail}) {
  const {address, primaryContact, pipeline} = group;
  const cityLine = [address.city, [address.state, address.zip].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
  // Keyed on the group so a revealed FEIN doesn't carry over when the page moves to another one.
  const [feinShownFor, setFeinShownFor] = useState<string | null>(null);
  const feinShown = feinShownFor === group.vbaGroupId;

  return (
    <div className='space-y-4'>
      <Section title='Identity'>
        <FieldGrid>
          <Field label='Legal name' value={group.name} />
          <Field label='DBA' value={pipeline?.dba} />
          <Field
            label='FEIN'
            value={
              group.fein && (
                <MaskedFein
                  fein={group.fein}
                  shown={feinShown}
                  onToggle={(shown) => setFeinShownFor(shown ? group.vbaGroupId : null)}
                />
              )
            }
            copyText={feinShown ? group.fein : null}
          />
          <Field label='VBA group ID' value={group.vbaGroupId} />
          <Field label='Agent ID' value={group.admin123AgentId} />
          <Field label='WLT group number' value={group.wltGroupNumber} />
          <Field label='Tenant' value={group.tenant?.name} />
          <Field label='State of domicile' value={address.state} />
          <Field
            label='Address'
            copyText={[address.line1, address.line2, cityLine].filter(Boolean).join(', ')}
            value={
              address.line1 && (
                <>
                  {address.line1}
                  {address.line2 && <br />}
                  {address.line2}
                  {cityLine && <br />}
                  {cityLine}
                </>
              )
            }
          />
          <Field label='Industry' value={pipeline?.industry} />
          <Field label='Payroll system' />
        </FieldGrid>

        <div className='mt-5 border-t pt-5'>
          <h3 className='mb-3 text-sm font-semibold'>Primary contact</h3>
          <FieldGrid>
            <Field
              label='Name'
              copyText={primaryContact.name}
              value={
                primaryContact.name && (
                  <>
                    {primaryContact.name}
                    {pipeline?.primaryContactTitle && (
                      <span className='block text-gray-500'>{pipeline.primaryContactTitle}</span>
                    )}
                  </>
                )
              }
            />
            <Field label='Email' value={primaryContact.email} />
            <Field label='Phone' value={primaryContact.phone} />
          </FieldGrid>
        </div>
      </Section>

      <Section title='Plan year and coverage'>
        <FieldGrid>
          <Field label='Plan year start' value={formatDateOrNull(group.planYear?.start)} />
          <Field label='Plan year end' value={formatDateOrNull(group.planYear?.end)} />
          <Field label='Effective date' value={formatDateOrNull(group.effectiveDate)} />
          <Field label='Anticipated date' value={formatDateOrNull(pipeline?.anticipatedDate)} />
          <Field
            label='Open enrollment'
            value={
              pipeline?.openEnrollmentStartDate &&
              `${formatDate(pipeline.openEnrollmentStartDate)} to ${formatDate(pipeline.openEnrollmentEndDate)}`
            }
          />
          <Field label='Billing cadence' value={group.billingCadence} />
          <Field label='Pay schedule' />
          <Field label='Total employees' value={group.totalEmployees?.toLocaleString()} />
          <Field label='Members' value={group.members.toLocaleString()} />
          <Field label='RFC received' value={formatDateOrNull(pipeline?.receivedAt)} />
          <Field
            label='Went live'
            value={pipeline ? (pipeline.liveAt ? formatDate(pipeline.liveAt) : 'Not yet') : null}
          />
        </FieldGrid>
      </Section>

      <GroupNotes vbaGroupId={group.vbaGroupId} />
    </div>
  );
}

// Hidden by default so it isn't on screen during calls or screen shares.
function MaskedFein({
  fein,
  shown,
  onToggle,
}: {
  fein: string;
  shown: boolean;
  onToggle: (shown: boolean) => void;
}) {
  return (
    <span className='inline-flex items-center gap-2'>
      <span className='tabular-nums'>{shown ? fein : `**-***${fein.slice(-4)}`}</span>
      <button
        type='button'
        onClick={(e) => {
          e.stopPropagation();
          onToggle(!shown);
        }}
        className='text-xs font-semibold text-blue-600 hover:underline'
      >
        {shown ? 'Hide' : 'Show'}
      </button>
    </span>
  );
}

const formatDateOrNull = (value: Date | string | null | undefined) =>
  value ? formatDate(value) : null;

function Section({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className='rounded-lg border bg-white p-5'>
      <div className='mb-4 flex items-center justify-between gap-2'>
        <h2 className='text-base font-semibold'>{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function FieldGrid({children}: {children: ReactNode}) {
  // Columns follow the card's own width (the sidebar makes viewport breakpoints unreliable):
  // as many 13rem columns as fit, up to four.
  return (
    <div className='grid grid-cols-[repeat(auto-fill,minmax(max(13rem,calc((100%_-_4.5rem)/4)),1fr))] gap-x-6 gap-y-4'>
      {children}
    </div>
  );
}

// Clicking a value copies it, unless the click ended a text selection. copyText overrides what's
// copied for values that aren't plain text; null makes the field not copyable.
function Field({
  label,
  value,
  copyText,
}: {
  label: string;
  value?: ReactNode;
  copyText?: string | null;
}) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1200);
    return () => clearTimeout(timer);
  }, [copied]);

  const empty = value === null || value === undefined || value === '' || value === false;
  const plain = typeof value === 'string' || typeof value === 'number' ? String(value) : null;
  const text = empty ? null : copyText !== undefined ? copyText : plain;

  const copy = () => {
    if (!text || window.getSelection()?.toString()) return;
    void navigator.clipboard.writeText(text).then(() => setCopied(true));
  };

  return (
    <div className='min-w-0'>
      <p className='mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500'>{label}</p>
      {empty ? (
        <div className='text-sm text-gray-400'>Not on file</div>
      ) : (
        <div
          onClick={copy}
          className={`group/copy -mx-1.5 flex items-start justify-between gap-1.5 rounded px-1.5 py-0.5 text-sm font-medium text-gray-900 [overflow-wrap:anywhere] ${text ? 'cursor-pointer hover:bg-gray-100' : ''}`}
        >
          <div className='min-w-0'>{value}</div>
          {text && (
            <button
              type='button'
              aria-label={copied ? `Copied ${label}` : `Copy ${label}`}
              onClick={(e) => {
                e.stopPropagation();
                copy();
              }}
              className={`mt-0.5 shrink-0 ${copied ? 'text-green-600' : 'text-gray-400 opacity-0 focus:opacity-100 group-hover/copy:opacity-100'}`}
            >
              {copied ? <Check className='size-3.5' /> : <Copy className='size-3.5' />}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
